import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { flushQueue, getQueueSize } from "@/lib/progressQueue";

/* Background sync for the offline progress-save queue.
 *
 * Renders nothing. Watches for events that indicate "the network is probably
 * usable now" and attempts to flush queued score-saves in the background:
 *
 *   1. On mount (after auth resolves) — catches queues left over from the
 *      previous session, e.g. a browser tab that was closed offline.
 *   2. On `online` window event — the browser reports connectivity is back.
 *   3. When the user's auth state transitions from anon → signed-in — a
 *      score saved while signed-out (guest path) is not supported, but if
 *      the queue was left over from a prior signed-in session and they log
 *      back in, this catches it.
 *
 * We only flush when there IS an authenticated user, because `/api/progress`
 * requires auth. If the queue exists but the user is signed out, we do
 * nothing — the queue survives across sessions in localStorage.
 *
 * Uses a mutex ref so overlapping triggers (mount + online event firing
 * simultaneously) don't double-flush the same item. */

export function ProgressQueueSync() {
  const { user, loading } = useAuth();
  const flushingRef = useRef(false);
  const [, setTick] = useState(0);

  const doFlush = useCallback(async () => {
    if (flushingRef.current) return;
    if (!user) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    if (getQueueSize() === 0) return;

    flushingRef.current = true;
    try {
      const result = await flushQueue(async (payload) => {
        await api.post("/api/progress", payload);
      });
      // Nudge a re-render so any UI subscribed to queue size (future addition)
      // gets a chance to observe the change.
      setTick((t) => t + 1);

      if (result.sent > 0) {
        toast.success(
          result.sent === 1
            ? "1 saved score synced to your training record"
            : `${result.sent} saved scores synced to your training record`
        );
      }
      if (result.errors.length > 0) {
        // Server rejected these permanently (paywall lapsed, email got
        // unverified, etc.). Surface once so the user knows they were lost,
        // rather than silently discarding.
        toast.error(
          result.errors.length === 1
            ? "1 saved score couldn't be synced"
            : `${result.errors.length} saved scores couldn't be synced`,
          { description: "Your subscription or verification status may have changed." }
        );
      }
    } catch {
      // flushQueue itself doesn't throw; this is defence-in-depth.
    } finally {
      flushingRef.current = false;
    }
  }, [user]);

  // On mount (once auth has resolved), attempt a flush.
  useEffect(() => {
    if (loading) return;
    doFlush();
  }, [loading, doFlush]);

  // On network recovery, attempt a flush.
  useEffect(() => {
    const onOnline = () => { doFlush(); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [doFlush]);

  return null;
}

export default ProgressQueueSync;
